import {AppName, getAppName} from 'util/AppName';
import {SisName} from "util/SisName";
import {unitOfTime} from 'moment';
import {SisNameBS} from 'module/BSCollection/type/SisNameBS';
import {UserConfigDefinitionType} from 'module/UserConfig/type/UserConfigType';
import {StatusForecastUserConfigDict} from 'module/BSCollection/dict/StatusForecastUserConfigDict'
import {StTagZuUserConfigDict} from 'module/BSCollection/dict/StTagZuUserConfigDict'
import {TaskZuUserConfigDict} from 'module/BSCollection/dict/TaskZuUserConfigDict'
import {PersurlUserConfigDict} from 'module/BSCollection/dict/PersurlUserConfigDict'
import {BookingUserConfigDict} from 'module/BSCollection/dict/BookingUserConfigDict'
import {HolidayUserConfigDict} from 'module/BSCollection/dict/HolidayUserConfigDic'
import {IstZeitUserConfigDict} from 'module/BSCollection/dict/IstZeitUserConfig'
import {V2UserConfigDict} from "./V2UserConfigDict";
import {V2DataUserConfigDict} from "./V2DatauUserConfigDict";
import {PlanEventConfigDict} from 'module/BSCollection/dict/PlanEventConfigDict'
import {KokeyUserConfigDict} from 'module/BSCollection/dict/KokeyUserConfigDict'

export type BSCollectionConfigType = {
	label: string,
	url: string,
	ownerAttr: string,
	ownerUrlParam: string,
	ownerPluralLabel: string,
	extendsOver: unitOfTime.Base,
	fetchAlways: boolean,
	fetchBoundary: unitOfTime.Base,
	fetchExpand: number, // in ms
	fetchOnlyVisibleRange: boolean,
	filterOnlyCanvas: boolean,
	filterOnlyVisible: boolean,
	hideOnScroll: boolean,
	indexScope: unitOfTime.Base,
	interval: number, // in ms
	maxCacheCount: number,
	maxZoom: null | number, // in ms *** when implementing in fetch/visible-selector, also use epic to inform user!
	dateScopeOnly: boolean,
	userConfig: UserConfigDefinitionType
};

export type BSCollectionDictType = Record<SisNameBS, BSCollectionConfigType>

const minutes = 60 * 1000;
const hours = 60 * minutes;
const days = 24 * hours;
const dflt: Omit<BSCollectionConfigType, 'userConfig'> = {
	label: '...',
	url: '',
	ownerAttr: 'persNr',
	ownerUrlParam: 'pnrLst',
	ownerPluralLabel: 'Personen',
	extendsOver: 'minute',
	fetchAlways: false,
	fetchBoundary: 'day',
	fetchExpand: 0, // in ms
	fetchOnlyVisibleRange: true,
	filterOnlyCanvas: true,
	filterOnlyVisible: false,
	hideOnScroll: false,
	indexScope: 'day',
	interval: 2 * minutes, // in ms
	maxCacheCount: 1000,
	maxZoom: null, // in ms
	dateScopeOnly: false,
};

// order of keys specifies displayed order in events!
export const BSCollectionDict: BSCollectionDictType = {
	[SisName.StTagZu]: {
		...dflt,
		label: 'Arbeitszeit',
		url: "../json/StTagZu",
		extendsOver: 'day',
		filterOnlyVisible: true,
		hideOnScroll: true,
		interval: 4 * hours,
		maxZoom: 32 * days,
		// maxCacheCount: 10000,
		fetchExpand: /*1*/days, // night-shift may have started on day before
		userConfig: StTagZuUserConfigDict,
	},
	[SisName.TaskZu]: {
		...dflt,
		label: "Taskzuweisungen",
		fetchBoundary: 'month',
		indexScope: 'month',
		...(getAppName() !== AppName.timelineTask ? {
			url: "../json/taskzu-pers",
		} : {
			url: "../json/taskzu",
			ownerAttr: 'task',
			ownerUrlParam: 'ids',
			ownerPluralLabel: 'Tasks',
			fetchAlways: true,
		}),
		userConfig: TaskZuUserConfigDict,
	},
	[SisName.Ist]: {
		...dflt,
		label: 'Ist-Zeit',
		url: '../json/istzeit',
		extendsOver: 'day',
		filterOnlyVisible: true,
		hideOnScroll: true,
		interval: 4 * hours,
		maxZoom: 32 * days,
		// maxCacheCount: 2000,
		fetchExpand: /*1*/days, // night-shift may have started on day before
		userConfig: IstZeitUserConfigDict,
	},
	[SisName.Persurl]: {
		...dflt,
		label: "Ganztägige Abwesenheiten",
		url: "../json/persurls",
		extendsOver: 'day',
		fetchBoundary: 'month',
		indexScope: 'month',
		filterOnlyCanvas: false,
		maxCacheCount: 500,
		userConfig: PersurlUserConfigDict,
	},
	[SisName.StatusForecast]: {
		...dflt,
		label: "Vorschau",
		url: "../json/status/forecast",
		maxZoom: 16 * days,
		userConfig: StatusForecastUserConfigDict,
	},
	[SisName.Booking]: {
		...dflt,
		label: "Buchungen",
		url: "../json/bookings",
		maxZoom: 16 * days,
		fetchExpand: 1, // overlapping bookings may start on day before view
		userConfig: BookingUserConfigDict,
	},
	[SisName.V2]: {
		...dflt,
		label: 'Verrechnungsergebnisse',
		url: '../json/v2',
		maxZoom: 16 * days,
		interval: 4 * hours,
		userConfig: V2UserConfigDict,
		hideOnScroll: true,
	},
	[SisName.V2Data]: {
		...dflt,
		label: "Kontierungen",
		url: "../json/azebooking/v2data",
		maxZoom: 16 * days,
		interval: 4 * hours,
		userConfig: V2DataUserConfigDict,
		hideOnScroll: true,
	},
	[SisName.Holiday]: {
		...dflt,
		label: "Feiertage",
		url: "../json/holiday/common",
		extendsOver: 'day',
		fetchAlways: true,
		fetchBoundary: 'month',
		indexScope: 'month',
		interval: 24 * hours,
		maxCacheCount: 500,
		dateScopeOnly: true,
		userConfig: HolidayUserConfigDict,
	},
	[SisName.PlanEvent]: {
		...dflt,
		url: "../json/planTemplate/active",
		label: "Buchungsplan",
		interval: 2 * minutes,
		maxZoom: 16 * days,
		userConfig: PlanEventConfigDict,
		hideOnScroll: true,
	},
	[SisName.KoKeyConf]: {
		...dflt,
		url: "../json/aze/view-config",
		label: "KoKey-Konfiguration",
		maxZoom: 32 * days,
		interval: 10 * minutes,
		userConfig: KokeyUserConfigDict,
	},
	// v2: {
	// 	...dflt,
	// 	url: "../json/v2",
	// 	label: "Bewertung",
	// },
	// [SisName.VlFehl]: {
	// 	...dflt,
	// 	url: "../json/aze-vlErrors",
	// 	label: "Verrechnungsfehler",
	// 	maxZoom: 16 * days,
	// 	userConfig: VlFehlUserConfigDict,
	// 	// hideOnScroll: true,
	// },
} as BSCollectionDictType
